/**
 * AdminNavigationConfig — Configuration des onglets de navigation par espace/rôle.
 *
 * Super Admin uniquement. Permet de :
 *  - Masquer/activer un onglet pour TOUS les utilisateurs d'un rôle (override "role")
 *  - Masquer/activer un onglet pour UN utilisateur nominatif (override "user", priorité)
 *
 * V36.3 — 28/05/2026.
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, Layers, Info, Search, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import BackButton from '../components/BackButton';
import { getAuthHeader } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SPACE_LABELS = {
  member: 'Espace Membre',
  cooper: 'Espace Cooper\'s',
  expert: 'Espace Expert',
  partenaire: 'Espace Partenaire',
  beneficiaire: 'Espace Bénéficiaire',
  super_admin: 'Espace Super Admin',
};

export default function AdminNavigationConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // clé "scope:target:tab" en cours
  const [catalog, setCatalog] = useState({});
  const [overrides, setOverrides] = useState([]);
  const [searchUser, setSearchUser] = useState('');
  const [foundUsers, setFoundUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const authHeader = () => getAuthHeader();

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, o] = await Promise.all([
        axios.get(`${API}/admin/navigation-config/catalog`, { headers: authHeader() }),
        axios.get(`${API}/admin/navigation-config/overrides`, { headers: authHeader() }),
      ]);
      setCatalog(c.data?.catalog || {});
      setOverrides(o.data?.overrides || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Map (scope:target:space:tab_key) → hidden bool
  const overrideMap = useMemo(() => {
    const m = new Map();
    for (const o of overrides) {
      m.set(`${o.scope}:${o.target}:${o.space}:${o.tab_key}`, !!o.hidden);
    }
    return m;
  }, [overrides]);

  const isHidden = (scope, target, space, tab_key) =>
    overrideMap.get(`${scope}:${target}:${space}:${tab_key}`) === true;

  const toggleVisibility = async ({ scope, target, space, tab_key, currentlyHidden }) => {
    const key = `${scope}:${target}:${space}:${tab_key}`;
    setSaving(key);
    try {
      const newHidden = !currentlyHidden;
      const url = scope === 'role'
        ? `${API}/admin/navigation-config/role/${space}/${tab_key}`
        : `${API}/admin/navigation-config/user/${target}/${space}/${tab_key}`;
      await axios.put(url, { hidden: newHidden }, { headers: authHeader() });
      toast.success(newHidden ? 'Onglet masqué' : 'Onglet rendu visible');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec de la modification');
    } finally {
      setSaving(null);
    }
  };

  const searchUsers = async () => {
    if (!searchUser.trim()) {
      setFoundUsers([]);
      return;
    }
    try {
      // Recherche simple via /api/membres
      const res = await axios.get(`${API}/membres`, { headers: authHeader() });
      const all = Array.isArray(res.data) ? res.data : (res.data?.members || res.data?.membres || []);
      const q = searchUser.toLowerCase();
      const filtered = all
        .filter((u) =>
          (u.email || '').toLowerCase().includes(q) ||
          (u.nom || '').toLowerCase().includes(q) ||
          (u.prenom || '').toLowerCase().includes(q) ||
          (u.id || '').toLowerCase().includes(q)
        )
        .slice(0, 8);
      setFoundUsers(filtered);
    } catch {
      toast.error('Recherche utilisateur impossible');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const spaces = Object.keys(catalog);

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4 sm:px-6 lg:px-8" data-testid="admin-navigation-config-page">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BackButton />
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Layers className="h-6 w-6 text-indigo-600" />
                Configuration de la navigation
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Masquez ou activez les onglets pour chaque espace, globalement ou par utilisateur.
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchAll} data-testid="nav-config-refresh">
            <RefreshCw className="h-4 w-4 mr-2" /> Actualiser
          </Button>
        </div>

        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4 flex gap-3 items-start text-sm text-blue-900">
            <Info className="h-5 w-5 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Comment ça marche ?</p>
              <p className="text-blue-800 mt-1">
                <strong>Override par rôle</strong> = applique à tous les utilisateurs du rôle.
                <strong> Override par utilisateur</strong> = surcharge le rôle pour un utilisateur précis.
                Si aucun override n'est défini, l'onglet est <strong>visible par défaut</strong>.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* OVERRIDES PAR RÔLE */}
        {spaces.map((space) => (
          <Card key={space} data-testid={`nav-config-space-${space}`}>
            <CardHeader>
              <CardTitle className="text-lg">{SPACE_LABELS[space] || space}</CardTitle>
              <CardDescription>Visibilité par défaut pour tous les utilisateurs de ce rôle.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-slate-100">
                {catalog[space].map((tab) => {
                  const hidden = isHidden('role', space, space, tab.key);
                  const key = `role:${space}:${space}:${tab.key}`;
                  const isSaving = saving === key;
                  return (
                    <div key={tab.key} className="flex items-center justify-between py-3" data-testid={`nav-config-tab-${space}-${tab.key}`}>
                      <div className="flex items-center gap-2">
                        {hidden
                          ? <EyeOff className="h-4 w-4 text-slate-400" />
                          : <Eye className="h-4 w-4 text-emerald-600" />}
                        <span className={`text-sm font-medium ${hidden ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                          {tab.label}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono ml-1">{tab.key}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {isSaving && <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />}
                        <Switch
                          checked={!hidden}
                          disabled={isSaving}
                          onCheckedChange={() => toggleVisibility({
                            scope: 'role', target: space, space, tab_key: tab.key,
                            currentlyHidden: hidden,
                          })}
                          data-testid={`nav-config-switch-${space}-${tab.key}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}

        {/* OVERRIDES PAR UTILISATEUR */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Override par utilisateur</CardTitle>
            <CardDescription>
              Surcharge nominative : prioritaire sur l'override de rôle. Recherchez un utilisateur par nom, prénom ou email.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-4">
              <Input
                placeholder="Rechercher (nom, prénom, email…)"
                value={searchUser}
                onChange={(e) => setSearchUser(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
                data-testid="nav-config-user-search-input"
              />
              <Button onClick={searchUsers} data-testid="nav-config-user-search-btn">
                <Search className="h-4 w-4 mr-2" /> Rechercher
              </Button>
            </div>

            {foundUsers.length > 0 && !selectedUser && (
              <div className="border rounded-lg divide-y divide-slate-100 mb-4">
                {foundUsers.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => setSelectedUser(u)}
                    className="w-full px-3 py-2 text-left hover:bg-slate-50 text-sm flex items-center justify-between"
                    data-testid={`nav-config-user-result-${u.id}`}
                  >
                    <span>
                      <span className="font-semibold">{u.prenom || ''} {u.nom || ''}</span>
                      <span className="text-slate-500 ml-2">({u.email})</span>
                    </span>
                    <span className="text-xs text-slate-400 font-mono">{u.id?.slice(0, 8)}…</span>
                  </button>
                ))}
              </div>
            )}

            {selectedUser && (
              <div className="border-2 border-indigo-200 rounded-lg p-4 bg-indigo-50/40">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-bold text-slate-900">{selectedUser.prenom || ''} {selectedUser.nom || ''}</p>
                    <p className="text-xs text-slate-500">{selectedUser.email} · ID: {selectedUser.id}</p>
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => { setSelectedUser(null); setSearchUser(''); setFoundUsers([]); }}
                    data-testid="nav-config-user-clear"
                  >
                    Changer
                  </Button>
                </div>

                {spaces.map((space) => (
                  <div key={space} className="mt-3 pt-3 border-t border-indigo-100">
                    <p className="text-xs font-semibold text-slate-600 uppercase mb-2">{SPACE_LABELS[space] || space}</p>
                    <div className="divide-y divide-slate-100 bg-white rounded">
                      {catalog[space].map((tab) => {
                        const userHidden = isHidden('user', selectedUser.id, space, tab.key);
                        const roleHidden = isHidden('role', space, space, tab.key);
                        const effectiveHidden = userHidden !== undefined && overrideMap.has(`user:${selectedUser.id}:${space}:${tab.key}`)
                          ? userHidden : roleHidden;
                        const key = `user:${selectedUser.id}:${space}:${tab.key}`;
                        const isSaving = saving === key;
                        return (
                          <div key={tab.key} className="flex items-center justify-between py-2 px-2 text-sm">
                            <span className={effectiveHidden ? 'text-slate-400' : 'text-slate-800'}>
                              {tab.label}
                              {overrideMap.has(`user:${selectedUser.id}:${space}:${tab.key}`) && (
                                <span className="ml-2 text-[10px] uppercase font-bold text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded">
                                  Override user
                                </span>
                              )}
                            </span>
                            <div className="flex items-center gap-2">
                              {isSaving && <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />}
                              <Switch
                                checked={!userHidden}
                                disabled={isSaving}
                                onCheckedChange={() => toggleVisibility({
                                  scope: 'user', target: selectedUser.id, space, tab_key: tab.key,
                                  currentlyHidden: userHidden,
                                })}
                                data-testid={`nav-config-user-switch-${space}-${tab.key}`}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
